import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import AddCardIcon from '@mui/icons-material/AddCard';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { providerApi } from '../../services/api';

const PENDING_TOP_UP_KEY = 'pendingTopUpId';

/**
 * Card showing the ad-credit pack ladder. The user picks a pack, clicks
 * "Add Credit", and is redirected to PayPal in the same tab.
 *
 * On return from PayPal (?topup_return=1), the component reads the pending
 * top-up ID from sessionStorage and captures the payment.
 */
const CreditTopUpCard = ({ onCaptureSuccess } = {}) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [balance, setBalance] = useState(null);      // { balanceCents, currency }
  const [balanceLoading, setBalanceLoading] = useState(true);

  const [packs, setPacks] = useState([]);
  const [packsLoading, setPacksLoading] = useState(true);
  const [packsError, setPacksError] = useState('');

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState('');
  const [captureSuccess, setCaptureSuccess] = useState('');

  const loadBalance = useCallback(async () => {
    setBalanceLoading(true);
    try {
      const data = await providerApi.getCreditAccount();
      setBalance(data);
    } catch (_) {
      // Balance display is best-effort; don't block the top-up UI on failure.
    } finally {
      setBalanceLoading(false);
    }
  }, []);

  // Load available credit packs from the server.
  const loadPacks = useCallback(async () => {
    setPacksLoading(true);
    setPacksError('');
    try {
      const data = await providerApi.getBillingProducts({ kind: 'CREDIT_PACK' });
      setPacks(data || []);
    } catch (e) {
      setPacksError(t('credit.packsLoadFailed'));
    } finally {
      setPacksLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadBalance();
    loadPacks();
  }, [loadBalance, loadPacks]);

  // On return from PayPal: capture the pending top-up.
  useEffect(() => {
    const isReturn = searchParams.get('topup_return') === '1';
    if (!isReturn) return;

    const topUpId = sessionStorage.getItem(PENDING_TOP_UP_KEY);

    // Clean up the URL param regardless.
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('topup_return');
      return next;
    }, { replace: true });

    if (!topUpId) return;

    setBusy(true);
    setActionError('');
    providerApi
      .captureCreditTopUp(topUpId)
      .then(() => {
        sessionStorage.removeItem(PENDING_TOP_UP_KEY);
        setCaptureSuccess(t('credit.captureSuccess'));
        loadBalance();
        if (onCaptureSuccess) onCaptureSuccess();
      })
      .catch(() => {
        sessionStorage.removeItem(PENDING_TOP_UP_KEY);
        setActionError(t('credit.captureFailed'));
      })
      .finally(() => setBusy(false));
    // Run only when topup_return param is present on mount/URL change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddCredit = async () => {
    const pack = packs[selectedIndex];
    if (!pack) return;
    setBusy(true);
    setActionError('');
    setCaptureSuccess('');
    try {
      const result = await providerApi.initiateCreditTopUp(pack.displayPriceCents);
      sessionStorage.setItem(PENDING_TOP_UP_KEY, result.topUpId);
      window.location.assign(result.approveUrl);
    } catch (e) {
      setActionError(t('credit.initiateFailed'));
      setBusy(false);
    }
  };

  const formatPrice = (cents, currency) => {
    if (cents == null) return '';
    const symbol = currency === 'ILS' ? '₪' : currency;
    return `${symbol}${(cents / 100).toFixed(0)}`;
  };

  const selectedPack = packs[selectedIndex];

  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <CardContent sx={{ p: 3 }}>
        {/* Header + balance */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddCardIcon sx={{ color: 'primary.main', fontSize: 22 }} />
            <Typography variant="subtitle1" fontWeight={700}>
              {t('credit.title')}
            </Typography>
          </Box>
          {/* Current balance */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccountBalanceWalletIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            {balanceLoading ? (
              <Skeleton variant="text" width={60} />
            ) : balance ? (
              <Chip
                label={`${t('credit.balance')}: ${formatPrice(balance.balanceCents, balance.currency)}`}
                size="small"
                color={balance.balanceCents > 0 ? 'success' : 'default'}
                variant="outlined"
              />
            ) : null}
          </Box>
        </Box>
        <Divider sx={{ mb: 2.5 }} />

        {captureSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {captureSuccess}
          </Alert>
        )}

        {actionError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {actionError}
          </Alert>
        )}

        {/* Pack grid */}
        {packsLoading ? (
          <Stack direction="row" spacing={1} sx={{ mb: 2.5 }}>
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rounded" width={72} height={52} />
            ))}
          </Stack>
        ) : packsError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {packsError}
          </Alert>
        ) : packs.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('credit.noPacks')}
          </Typography>
        ) : (
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2.5, gap: 1 }}>
            {packs.map((pack, idx) => {
              const isSelected = idx === selectedIndex;
              const hasDiscount = pack.listPriceCents > pack.displayPriceCents;
              return (
                <Box
                  key={pack.skuCode}
                  role="button"
                  aria-pressed={isSelected}
                  onClick={() => setSelectedIndex(idx)}
                  sx={{
                    cursor: 'pointer',
                    border: '2px solid',
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    borderRadius: 2,
                    px: 2,
                    py: 1,
                    minWidth: 72,
                    textAlign: 'center',
                    bgcolor: isSelected ? 'primary.50' : 'background.paper',
                    transition: 'border-color 0.15s, background-color 0.15s',
                    '&:hover': { borderColor: 'primary.light' },
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    color={isSelected ? 'primary.main' : 'text.primary'}
                  >
                    {formatPrice(pack.displayPriceCents, pack.currency)}
                  </Typography>
                  {hasDiscount && (
                    <Typography
                      variant="caption"
                      sx={{ textDecoration: 'line-through', color: 'text.disabled', display: 'block' }}
                    >
                      {formatPrice(pack.listPriceCents, pack.currency)}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}

        {/* Action button */}
        {!packsLoading && !packsError && packs.length > 0 && (
          <Button
            variant="contained"
            onClick={handleAddCredit}
            disabled={busy || !selectedPack}
            size="large"
            startIcon={busy ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {busy
              ? t('credit.redirecting')
              : selectedPack
              ? t('credit.addButton', { amount: formatPrice(selectedPack.displayPriceCents, selectedPack.currency) })
              : t('credit.addButtonGeneric')}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default CreditTopUpCard;
